import * as React from "react";
import { cn } from "../cn";

export interface Column<T> {
  key: string;
  header: string;
  /** Cell renderer. */
  cell: (row: T) => React.ReactNode;
  /** "num" right-aligns and locks tabular figures so digit columns line up. */
  align?: "left" | "right" | "center" | "num";
  className?: string;
  /** Header className override (e.g. width). */
  headerClassName?: string;
}

function alignClass(align?: Column<unknown>["align"]) {
  return align === "right" || align === "num" ? "text-right" : align === "center" ? "text-center" : "text-left";
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  density = "default",
  empty,
  className,
  zebra = false,
  stickyHeader = false,
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  density?: "default" | "compact" | "spacious";
  empty?: React.ReactNode;
  className?: string;
  /** Tint even rows for long tables. */
  zebra?: boolean;
  /** Pin the header on scroll (for tall, scrolling tables). */
  stickyHeader?: boolean;
}) {
  const pad = density === "compact" ? "px-3 py-2" : density === "spacious" ? "px-4 py-3.5" : "px-4 py-3";
  if (rows.length === 0 && empty) {
    return <div className={className}>{empty}</div>;
  }
  return (
    <div className={cn("overflow-x-auto rounded-lg border border-line bg-card shadow-card", className)}>
      <table className="w-full min-w-[34rem] border-collapse text-sm">
        <thead>
          <tr className={cn("border-b border-line bg-subtle/70", stickyHeader && "sticky top-0 z-10 shadow-sticky backdrop-blur")}>
            {columns.map((c) => (
              <th
                key={c.key}
                className={cn(pad, "text-meta text-faint", alignClass(c.align), c.headerClassName)}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={rowKey(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={cn(
                "border-b border-hairline last:border-0 transition-colors duration-150",
                zebra && i % 2 === 1 && "bg-neutral25",
                onRowClick && "cursor-pointer hover:bg-rowHover hover:[box-shadow:inset_2px_0_0_var(--accent-line)]",
              )}
            >
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={cn(
                    pad,
                    "align-middle text-ink",
                    alignClass(c.align),
                    // Numeric / right-aligned columns never wrap their figures.
                    (c.align === "num" || c.align === "right") && "whitespace-nowrap",
                    c.align === "num" && "num",
                    c.className,
                  )}
                >
                  {c.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
